/**
 * assetUploadDispatch — extension → wizard mapping.
 *
 * When a user drops one or more files onto the ContentBrowser, this module
 * is the single entry point that picks the right wizard. Unreal-style: when
 * an extension is ambiguous (.glb could be a rig OR a body anim OR a face
 * anim with embedded morph tracks), we hand off to DisambiguateGlbStep
 * which asks the user before continuing.
 *
 * Wizards register a config:
 *   { component, extensions: string[], label }
 *
 * The dispatcher returns a queue of { file, wizardKey } pairs that the
 * ContentBrowser walks one at a time. Sequential, not parallel — wizards
 * have UI and we don't want N modals stacked.
 */

const _registry = new Map() // wizardKey -> { component, extensions, label }
const _extIndex = new Map() // ext (no dot) -> wizardKey[]

export function registerWizard(key, { component, extensions, label }) {
  if (!key) throw new Error('registerWizard: key required')
  // component may be null for placeholder slots that only claim an extension
  // (e.g. the 'rig' slot reserves '.glb' for DisambiguateGlbStep without
  // having its own form yet — see ContentBrowser's 'rig' fallback modal).
  _registry.set(key, { component: component ?? null, extensions: extensions ?? [], label: label ?? key })
  for (const ext of extensions ?? []) {
    const norm = ext.replace(/^\./, '').toLowerCase()
    const list = _extIndex.get(norm) ?? []
    list.push(key)
    _extIndex.set(norm, list)
  }
}

export function getWizard(key) {
  return _registry.get(key)
}

export function listWizards() {
  return Array.from(_registry.entries()).map(([key, v]) => ({ key, ...v }))
}

// Return candidate wizard keys for a given filename, in priority order.
// When .length > 1 the dispatcher MUST ask the user (DisambiguateGlbStep).
export function candidatesFor(filename) {
  const m = /\.([a-z0-9]+)$/i.exec(filename ?? '')
  if (!m) return []
  return _extIndex.get(m[1].toLowerCase()) ?? []
}

// ── Content sniffing ────────────────────────────────────────────────────────
//
// An extension cannot decide what a `.json` is: `face-anim` already claims it
// (ARKit face-clip weights), and a blueprint export is `.json` too. Registering
// both would make candidatesFor() return two keys, which maps to
// 'disambiguate-glb' — a key with no registered component, so ContentBrowser
// would warn and silently advance, dropping every JSON the user drags in.
//
// So a multi-candidate JSON is decided by its CONTENT. The user is never asked
// a question the bytes already answer, and no working drop path is broken to
// add a new one.
//
// Only the head is read: a blueprint export can be megabytes, and the tag is in
// the first object.
const SNIFF_BYTES = 64 * 1024

/** Wizard keys whose files identify themselves by a top-level JSON key. */
const JSON_SIGNATURES = [
  { key: 'blueprint-import', tag: 'aosBlueprintClip' },
]

function isJsonName(name) {
  return /\.json$/i.test(name ?? '')
}

async function sniffJsonKey(file) {
  try {
    const head = await file.slice(0, SNIFF_BYTES).text()
    const trimmed = head.trimStart()
    if (!trimmed.startsWith('{')) return null
    // The head is very likely truncated mid-object, so a full parse will fail
    // on any large file. Look for the tag as a KEY instead — cheap, and a
    // false positive would need the literal key to appear in another format's
    // first 64 KB.
    for (const { key, tag } of JSON_SIGNATURES) {
      if (new RegExp(`"${tag}"\\s*:`).test(head)) return key
    }
    return null
  } catch {
    // An unreadable file is not a classification failure — let it fall through
    // to the extension candidates, where the wizard reports the real problem.
    return null
  }
}

/**
 * Build a sequential queue from a FileList. Each entry is the file + the chosen
 * wizard key, or 'ambiguous-extension' when the extension is claimed by several
 * wizards and the content did not settle it, or 'unknown-extension' when
 * nothing claims it. The ContentBrowser pops from this queue and mounts the
 * matching wizard.
 *
 * ASYNC since the content sniff reads bytes. Callers must await it.
 */
export async function buildUploadQueue(files, defaultPath = []) {
  const queue = []
  for (const file of files) {
    const cands = candidatesFor(file.name)
    let wizardKey

    // A self-identifying file wins over its extension, whether or not the
    // extension is ambiguous — a blueprint export named .json must never reach
    // the face-clip wizard.
    const sniffed = isJsonName(file.name) ? await sniffJsonKey(file) : null

    if (sniffed) wizardKey = sniffed
    else if (cands.length === 0) wizardKey = 'unknown-extension'
    else if (cands.length === 1) wizardKey = cands[0]
    else {
      // Several wizards claim the extension and nothing in the bytes decided.
      // Drop the keys that only match by self-identification, since we just
      // proved this file does not identify as one of them.
      const signatureKeys = new Set(JSON_SIGNATURES.map(s => s.key))
      const real = cands.filter(k => !signatureKeys.has(k))
      wizardKey = real.length === 1 ? real[0] : 'ambiguous-extension'
    }
    queue.push({ file, wizardKey, path: defaultPath, candidates: cands })
  }
  return queue
}
